"use client";

import { logger } from "@/utils/logger";
import { useEffect } from "react";

/**
 * Component that monitors for webpack chunk loading errors.
 * These can occur when:
 * - A new deployment happens while user has cached old chunks
 * - Network issues prevent chunk loading
 * - CDN cache invalidation issues in serverless environments
 *
 * This component should be placed in the root layout to catch all chunk errors.
 */
export function WebpackChunkErrorMonitor() {
	useEffect(() => {
		if (typeof window === "undefined") return;

		const handleError = (event: ErrorEvent) => {
			// Check for actual webpack chunk loading errors
			const isActualChunkError =
				event.message?.includes("webpack") ||
				event.message?.includes("Loading chunk") ||
				event.message?.includes("ChunkLoadError") ||
				event.message?.includes("Loading CSS chunk");

			// Check if it's a hydration mismatch error from Next.js bundles
			// React error codes for hydration mismatches: #418, #423, #425
			const isHydrationError =
				event.message?.includes("Hydration") ||
				event.message?.includes("#418") ||
				event.message?.includes("#423") ||
				event.message?.includes("#425");

			// Only handle webpack chunk errors, not hydration mismatches
			const isWebpackError = isActualChunkError && !isHydrationError;

			// Log hydration errors to console only (not to Logtail) - these are common and non-critical
			if (isHydrationError && event.filename?.includes("/_next/")) {
				// eslint-disable-next-line no-console
				console.warn("React hydration error:", {
					message: event.message,
					filename: event.filename,
					lineno: event.lineno,
					colno: event.colno,
					url: window.location.href,
					timestamp: new Date().toISOString(),
				});
				return;
			}

			if (isWebpackError) {
				logger.warn("Webpack chunk error detected:", {
					message: event.message,
					filename: event.filename,
					lineno: event.lineno,
					colno: event.colno,
					url: window.location.href,
					timestamp: new Date().toISOString(),
				});

				// Optionally trigger a page reload for chunk load failures
				// This helps when there's a deployment mismatch
				if (
					event.message?.includes("Loading chunk") ||
					event.message?.includes("ChunkLoadError") ||
					event.message?.includes("Loading CSS chunk")
				) {
					// Store reload attempt count to prevent infinite reload loops
					const reloadKey = "webpack_chunk_reload_attempt";
					const reloadCount = parseInt(sessionStorage.getItem(reloadKey) || "0", 10);

					if (reloadCount < 2) {
						sessionStorage.setItem(reloadKey, String(reloadCount + 1));
						logger.warn("Attempting page reload due to chunk load failure", {
							attempt: reloadCount + 1,
						});
						// Small delay before reload to allow error logging to complete
						setTimeout(() => {
							window.location.reload();
						}, 100);
					} else {
						logger.error("Max reload attempts reached for chunk error", {
							attempts: reloadCount,
						});
						// Clear the counter after max attempts
						sessionStorage.removeItem(reloadKey);
					}
				}
			}
		};

		const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
			const reason = event.reason;
			const isChunkError =
				reason?.message?.includes("Loading chunk") ||
				reason?.message?.includes("ChunkLoadError") ||
				reason?.name === "ChunkLoadError";

			if (isChunkError) {
				logger.error("Webpack chunk promise rejection:", {
					message: reason?.message,
					name: reason?.name,
					stack: reason?.stack,
					url: window.location.href,
					timestamp: new Date().toISOString(),
				});
			}
		};

		window.addEventListener("error", handleError);
		window.addEventListener("unhandledrejection", handleUnhandledRejection);

		// Clear reload counter on successful page load
		const reloadKey = "webpack_chunk_reload_attempt";
		const timer = setTimeout(() => {
			sessionStorage.removeItem(reloadKey);
		}, 5000);

		return () => {
			window.removeEventListener("error", handleError);
			window.removeEventListener("unhandledrejection", handleUnhandledRejection);
			clearTimeout(timer);
		};
	}, []);

	return null;
}
